<script setup lang="ts">
import { VERSES_PER_THEME_ROUND } from '~/stores/swordDrill'

const props = withDefaults(defineProps<{
  interactive?: boolean
  presentationConnected?: boolean
  showAnswersByDefault?: boolean
}>(), {
  interactive: true,
  presentationConnected: false,
  showAnswersByDefault: false,
})

const store = useSwordDrillStore()
const { data: ui } = useSwordDrillUI()

const isThematic = computed(() => store.config.mode === 'thematic')

const modeLabel = computed(() => {
  if (!ui.value) {
    return ''
  }
  switch (store.config.mode) {
    case 'new-testament': return ui.value.gameScreen.modeNewTestamentLabel
    case 'old-testament': return ui.value.gameScreen.modeOldTestamentLabel
    case 'thematic': return ui.value.gameScreen.modeThematicLabel
    default: return ui.value.gameScreen.modeBibleLabel
  }
})

const reference = computed(() => store.currentVerse?.reference ?? '')
const verseText = computed(() => store.currentVerse?.text ?? '')

const onVerse = computed(() => store.phase === 'verse' && !!store.currentVerse)
const onThemeQuestion = computed(() => store.phase === 'themeQuestion' && !!store.currentTheme)

// Whether the answer body (verse text or theme answer) is visible. The
// presenter view uses `revealed` strictly so the audience only sees the
// answer once the leader steps; the controller can be configured to peek
// ahead via `showAnswersByDefault` (set when a presentation is connected).
const showAnswer = computed(() => props.showAnswersByDefault || store.revealed)

// Single stepping button: tap reveals, tap again advances. The label
// reflects which step is next so the leader always knows what the next
// tap will do. "Reveal Answer" matches Showdown's mini-game wording so
// the verb is consistent across the library.
const stepLabel = computed(() => {
  const strings = ui.value?.gameScreen
  if (!strings) {
    return ''
  }
  if (!store.revealed) {
    return strings.revealAnswer
  }
  return onThemeQuestion.value ? strings.nextThemeButton : strings.nextButton
})

// Key for the outer phase Transition. The inner verse-card / answer-card
// fade in/out independently when the leader reveals; this outer key only
// changes when the *whole panel* should swap (verse to verse, verse to
// theme question, theme question to next verse). Tying the key to the
// active reference (or theme name) gives us a clean cross-fade between
// items so the reference and revealed body fade out together instead of
// the reference snapping while the body slowly fades.
const phaseKey = computed(() => {
  if (onVerse.value) {
    return `verse:${reference.value}`
  }
  if (onThemeQuestion.value && store.currentTheme) {
    return `theme:${store.currentTheme.name}`
  }
  return 'idle'
})

function handleStep() {
  if (!props.interactive) {
    return
  }
  if (!store.revealed) {
    store.reveal()
  } else {
    store.next()
  }
}
</script>

<template>
  <div class="sfg-sword-stage relative flex h-screen w-screen flex-col items-center justify-center overflow-hidden p-[3vw]">
    <!-- Mode pill (top-center) -->
    <div class="absolute top-[3vw] left-1/2 -translate-x-1/2 flex items-center gap-3">
      <div class="rounded-full px-[2.5vw] py-[0.6vw] bg-amber-500 shadow-lg ring-2 ring-white/20">
        <span class="font-black uppercase tracking-widest text-white" style="font-size: clamp(0.85rem, 1.6vw, 2.4rem);">
          {{ modeLabel }}
        </span>
      </div>
      <!-- Thematic progress: "(N of 6)" -->
      <div
        v-if="isThematic && store.themeProgress !== null && onVerse"
        class="rounded-full px-[2vw] py-[0.6vw] bg-white/10 ring-2 ring-white/20"
      >
        <span class="font-black uppercase tracking-widest text-white/90 tabular-nums" style="font-size: clamp(0.8rem, 1.4vw, 2.2rem);">
          {{ store.themeProgress }} / {{ VERSES_PER_THEME_ROUND }}
        </span>
      </div>
    </div>

    <!-- Centered phase panel. The outer keyed Transition cross-fades the
         whole block when the active item changes (so the reference and a
         revealed verse leave together). The inner Transitions handle the
         reveal animation within a single item. -->
    <Transition name="fade" mode="out-in">
      <div :key="phaseKey" class="flex flex-col items-center gap-[3vw] max-w-[90%] text-center">
        <!-- Verse panel -->
        <template v-if="onVerse">
          <p
            class="font-black text-amber-400 drop-shadow-2xl"
            style="font-size: clamp(2.5rem, 7vw, 12rem);"
          >
            {{ reference }}
          </p>

          <Transition name="fade">
            <div
              v-if="showAnswer"
              class="rounded-2xl px-[3vw] py-[2vw] bg-amber-500/10 ring-2 ring-amber-500/40"
            >
              <p class="leading-relaxed text-white" style="font-size: clamp(1.2rem, 2.8vw, 5rem);">
                "{{ verseText }}"
              </p>
            </div>
          </Transition>
        </template>

        <!-- Theme question panel -->
        <template v-else-if="onThemeQuestion && store.currentTheme">
          <p
            class="font-black uppercase tracking-widest text-amber-400/80"
            style="font-size: clamp(0.95rem, 1.8vw, 2.6rem);"
          >
            {{ ui?.gameScreen.themeQuestionEyebrow }}
          </p>
          <p
            class="font-bold leading-snug text-white"
            style="font-size: clamp(1.6rem, 4vw, 7rem);"
          >
            {{ store.currentTheme.question }}
          </p>
          <Transition name="fade">
            <div
              v-if="showAnswer"
              class="rounded-2xl px-[3vw] py-[2vw] bg-amber-500/15 ring-2 ring-amber-500/50"
            >
              <p class="font-black text-amber-300" style="font-size: clamp(1.4rem, 3.4vw, 6rem);">
                {{ store.currentTheme.answer }}
              </p>
            </div>
          </Transition>
        </template>
      </div>
    </Transition>

    <!-- Stepping button (bottom-right). Held outside the panel transition
         so its position stays fixed while the verse/question fades. The
         label flips between "Reveal Answer" and the next-action verb the
         moment the leader taps. -->
    <button
      v-if="props.interactive && (onVerse || onThemeQuestion)"
      class="absolute bottom-[3vw] right-[3vw] rounded-2xl bg-white/10 px-[3vw] py-[1.5vw] font-black text-white ring-2 ring-white/30 transition hover:bg-white/20"
      style="font-size: clamp(1.2rem, 3vw, 5rem);"
      @click="handleStep"
    >
      {{ stepLabel }}
    </button>
  </div>
</template>

<style scoped>
.sfg-sword-stage {
  /* Deep slate background, same family as Showdown's stage so the in-game
     look feels coherent across games. */
  background: radial-gradient(
    ellipse at center,
    rgb(30 41 59) 0%,
    rgb(15 23 42) 70%,
    rgb(2 6 23) 100%
  );
  color: white;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
